import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useLanguage } from "../context/LanguageProvider";

export default function DeleteModal({ isOpen, onClose, onConfirm, title, message, itemName }) {
  const { t } = useLanguage();
  const modalRef = useRef(null);
  
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden'; 
    } else {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return createPortal(
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-[99] animate-fade-in"
      onClick={onClose} // Close if clicking outside the modal
    >
      <div
        ref={modalRef}
        className="bg-white rounded-lg shadow-xl p-6 w-full max-w-sm mx-auto animate-scale-up"
        onClick={(e) => e.stopPropagation()} // Prevent closing when clicking inside the modal
      >
        {/* Modal Header */}
        <div className="flex justify-between items-center pb-3 border-b border-gray-200 mb-4">
          <h3 className="text-sm font-semibold text-gray-800">{title}</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors duration-200 cursor-pointer"
            aria-label={t('cors_delete_modal.close_modal')}
          >
            <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
            </svg>
          </button>
        </div>

        {/* Modal Body */}
        <div className="mb-6">
          <p className="text-gray-600 text-base text-xs">
            {message} <strong className="text-gray-800">{itemName}</strong>?
          </p>
          <p className="text-xs text-gray-500 mt-2">{t('cors_delete_modal.cannot_undo')}</p>
        </div>

        {/* Modal Footer (Buttons) */}
        <div className="flex justify-end space-x-3">
          <button
            onClick={onClose}
            className="px-5 py-2 text-xs cursor-pointer rounded-lg text-gray-700 bg-gray-100 hover:bg-gray-200 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-gray-300"
          >
            {t('cors_delete_modal.cancel')}
          </button>
          <button
            onClick={onConfirm}
            className="px-5 py-2 text-xs cursor-pointer rounded-lg bg-red-600 text-white hover:bg-red-700 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-red-500"
          >
            {t('cors_delete_modal.delete')}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}